import React from 'react';

import { OrderCurrency, type OrderDTO } from '@invenco/sales-interface/interfaces';

import { Card, CardContent } from '@/components/core/card';
import { Separator } from '@/components/core/separator';
import { Skeleton } from '@/components/core/skeleton';
import { formatMoney } from '@/shared/helpers';

import { getOrderLineDiscountAmount, type OrderLine } from '../order-lines-card/order-line-row';

type OrderTotalsCardProps = {
  order?: OrderDTO;
  lines: OrderLine[];
  isLoading?: boolean;
};

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function topLevelLines(lines: OrderLine[]) {
  return lines.filter((line) => !line.assemblyOrderLineId);
}

function deriveSubtotal(lines: OrderLine[]) {
  return roundMoney(
    topLevelLines(lines).reduce(
      (sum, line) => sum + (line.qtyOrdered ?? 0) * (line.unitPrice ?? 0),
      0,
    ),
  );
}

function deriveDiscounts(lines: OrderLine[]) {
  return roundMoney(
    topLevelLines(lines).reduce((sum, line) => sum + getOrderLineDiscountAmount(line), 0),
  );
}

function deriveUnits(lines: OrderLine[]) {
  return topLevelLines(lines).reduce((sum, line) => sum + (line.qtyOrdered ?? 0), 0);
}

export function OrderTotalsCard({ order, lines, isLoading }: OrderTotalsCardProps) {
  const currency = order?.currency ?? OrderCurrency.AUD;
  const subtotal = order?.subtotalPrice ?? deriveSubtotal(lines);
  const discounts = order?.subtotalDiscounts ?? deriveDiscounts(lines);
  const shipping = order?.subtotalShipping ?? 0;
  const total = order?.totalPrice ?? roundMoney(subtotal + shipping - discounts);
  const units = order?.qtyOrdered ?? deriveUnits(lines);

  const subtotalRows = [
    {
      label: 'Subtotal',
      description: isLoading ? undefined : `${units} ${units === 1 ? 'unit' : 'units'}`,
      value: subtotal,
    },
    { label: 'Discounts', value: discounts },
    {
      label: 'Shipping',
      description: order?.shippingDescription ?? order?.shippingProfileName ?? undefined,
      value: shipping,
    },
  ];

  return (
    <Card aria-label="Totals">
      <CardContent>
        <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 tabular-nums">
          {subtotalRows.map(({ label, description, value }) => (
            <React.Fragment key={label}>
              <dt className="min-w-0">
                {label}
                {description ? (
                  <span className="wrap-break-word text-muted-foreground"> · {description}</span>
                ) : null}
              </dt>
              <dd className="text-right">
                {isLoading ? (
                  <Skeleton className="ml-auto h-4 w-20" aria-label="Loading total amount" />
                ) : (
                  formatMoney(value, currency)
                )}
              </dd>
            </React.Fragment>
          ))}
          <div className="col-span-full -mx-4 py-2">
            <Separator />
          </div>
          <dt className="font-semibold">Order total</dt>
          <dd className="text-right font-semibold">
            {isLoading ? (
              <Skeleton className="ml-auto h-4 w-20" aria-label="Loading total amount" />
            ) : (
              formatMoney(total, currency)
            )}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}
